/**
 * Ory self-hosted completeness surfacing on report/dashboard.
 */
export default function OryCompletenessReport({ completeness, normalizedFindings = [] }) {
  if (!completeness || typeof completeness !== "object") return null;

  const flag = String(completeness.assessment_flag || "");
  const stale = Boolean(completeness.attestation_stale);
  const matrix = completeness.coverage_matrix || {};
  const unverified = Array.isArray(completeness.unverified) ? completeness.unverified : [];
  const scoreLine = completeness.score_coverage?.line;

  const notVisibleFindings = (Array.isArray(normalizedFindings) ? normalizedFindings : []).filter(
    (f) => String(f?.status || "").toUpperCase() === "NOT_VISIBLE",
  );

  const showItems =
    unverified.length > 0
      ? unverified
      : notVisibleFindings.map((f) => ({
          rule_id: f.rule_id,
          title: f.title,
          reason: f.adapter_extension?.not_visible_reason || "not_visible",
          resolve_hint: f.adapter_extension?.resolve_hint || f.description,
        }));

  return (
    <section style={{ marginBottom: "20px" }}>
      {flag === "INCOMPLETE" ? (
        <div
          style={{
            marginBottom: "14px",
            padding: "12px 14px",
            borderRadius: "8px",
            border: "1px solid var(--warning)",
            background: "rgba(234,179,8,0.08)",
            fontSize: "13px",
            lineHeight: 1.45,
            color: "var(--text-muted)",
          }}
          role="alert"
        >
          <strong style={{ color: "var(--text-strong)" }}>
            Potentially incomplete configuration
          </strong>
          <p style={{ margin: "6px 0 0" }}>
            The following could not be assessed and may represent gaps to confirm. The posture score
            only reflects checks we could verify.
          </p>
          {Array.isArray(completeness.gaps) && completeness.gaps.length > 0 ? (
            <ul style={{ margin: "8px 0 0", paddingLeft: "18px" }}>
              {completeness.gaps.map((g) => (
                <li key={g}>{g}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {stale ? (
        <p className="text-dim" style={{ fontSize: "12px", marginBottom: "10px" }}>
          Attestation is stale (connection inputs changed). Re-confirm scope before the next interactive run.
        </p>
      ) : null}

      {scoreLine ? (
        <p className="text-secondary" style={{ fontSize: "12px", marginBottom: "12px" }}>
          {scoreLine}
        </p>
      ) : null}

      <div style={{ marginBottom: "14px" }}>
        <h3 style={{ fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
          Coverage matrix
        </h3>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: "11px", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--text-dim)" }}>
                <th style={{ padding: "4px 8px" }}>Module</th>
                <th style={{ padding: "4px 8px" }}>Endpoint</th>
                <th style={{ padding: "4px 8px" }}>Admin API</th>
                <th style={{ padding: "4px 8px" }}>Config upload</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(matrix).map(([mod, row]) => (
                <tr key={mod} style={{ borderTop: "1px solid var(--border)" }}>
                  <td style={{ padding: "6px 8px" }}>{mod}</td>
                  <td style={{ padding: "6px 8px" }}>{row.endpoint_provided ? "✓" : "—"}</td>
                  <td style={{ padding: "6px 8px" }}>{row.admin_api ? "✓" : "—"}</td>
                  <td style={{ padding: "6px 8px" }}>{row.config_upload ? "✓" : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 style={{ fontSize: "14px", fontWeight: 600, marginBottom: "8px" }}>
          Could not verify / not in scope
        </h3>
        {showItems.length === 0 ? (
          <p className="text-dim" style={{ fontSize: "12px" }}>
            All in-scope checks were verifiable from provided inputs.
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "12px", lineHeight: 1.5 }}>
            {showItems.map((item) => (
              <li key={item.rule_id || item.title} style={{ marginBottom: "8px" }}>
                <strong>{item.title || item.rule_id}</strong>
                {item.reason ? ` — ${item.reason}` : null}
                {item.resolve_hint ? (
                  <div className="text-dim" style={{ marginTop: "2px" }}>
                    {item.resolve_hint}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
